import * as React from "react";

import { Input } from "@/components/ui/input";

interface CommittedInputProps extends Omit<
  React.ComponentProps<typeof Input>,
  "value" | "onChange"
> {
  value: string;
  onCommit: (raw: string) => void;
}

/**
 * Input for a server-backed value: commits on blur or Enter, never per keystroke.
 * Escape reverts. Input the browser rejects (out of `min`/`max`, unparseable
 * number) reverts on blur rather than committing.
 */
export function CommittedInput({
  value,
  onCommit,
  onBlur,
  onKeyDown,
  ...props
}: CommittedInputProps) {
  const [draft, setDraft] = React.useState(value);

  React.useEffect(() => setDraft(value), [value]);

  return (
    <Input
      {...props}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={(e) => {
        const { badInput, rangeOverflow, rangeUnderflow } =
          e.currentTarget.validity;
        if (badInput || rangeOverflow || rangeUnderflow) setDraft(value);
        else if (draft !== value) onCommit(draft);
        onBlur?.(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        else if (e.key === "Escape") setDraft(value);
        onKeyDown?.(e);
      }}
    />
  );
}
